import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Divider } from 'antd';
import { isAxiosError } from 'axios';
import { capitalCase } from 'change-case';
import { TbChartPie } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { FullHeightFlexColumn, LearnMoreFooter, NotAvailable, OurDocsLink, PlatformAlert } from '@/shared';
import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import { WorkspaceActivityTimeline } from '@/shared/components/data-visualization/visx';
import type {
    ActivityTimelineCadenceId,
    WorkspaceActivityTimelineEvent,
} from '@/shared/components/data-visualization/visx';
import { useWorkspaceStore } from '@/stores';

import AdvancedSettingsViewHeader from '../components/AdvancedSettingsViewHeader';
import { UsageOverviewSkeleton } from './components/UsageAnalyticsSkeletons';
import UsageLocationsMap from './components/UsageLocationsMap';
import UsageOverviewStats from './components/UsageOverviewStats';
import UsagePeriodDropdown from './components/UsagePeriodDropdown';
import UsageResourcesTable from './components/UsageResourcesTable';
import UsageTimelineChart from './components/UsageTimelineChart';
import { GeoLocation, UsageTimeFrame, WorkspaceUsage } from './types';

import 'maplibre-gl/dist/maplibre-gl.css';

const Usage: FC = (): JSX.Element => {
    const { token, resolvedThemeName } = useAntDesignTheme();
    const { hasMinimumPlan, planTier } = useEntitlements();
    const { selectedWorkspace: workspace } = useWorkspaceStore();

    const [loading, setLoading] = useState(true);
    const [usageError, setUsageError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [usageData, setUsageData] = useState<WorkspaceUsage | null>(null);
    const [timeFrame, setTimeFrame] = useState<UsageTimeFrame>(UsageTimeFrame.WEEK);
    const [timelineCadence, setTimelineCadence] = useState<ActivityTimelineCadenceId>('week');
    const [popupInfo, setPopupInfo] = useState<GeoLocation | null>(null);

    const mapTileType =
        resolvedThemeName === 'dark'
            ? 'https://tiles.openfreemap.org/styles/dark'
            : 'https://tiles.openfreemap.org/styles/positron';
    useEffect(() => {
        const controller = new AbortController();

        const handleGetUsageAnalytics = async () => {
            try {
                setLoading(true);
                setUsageError(false);

                const response = await authorizedAxiosInstance.get<WorkspaceUsage>('/api/usage', {
                    params: { timeframe: timeFrame },
                    signal: controller.signal,
                });

                if (response?.status === 200) {
                    setUsageData(response.data);
                }
            } catch (error) {
                if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
                    return;
                }

                captureException(error);
                setUsageError(true);
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        void handleGetUsageAnalytics();

        return () => controller.abort();
    }, [retryCount, timeFrame]);

    const resourceIdToType = useMemo(
        () =>
            (usageData?.resources ?? []).reduce<Record<string, string>>((acc, resource) => {
                acc[resource.resourceId] = resource.resourceType || 'any';

                return acc;
            }, {}),
        [usageData?.resources],
    );
    const resourceNameById = useMemo(() => {
        const names = new Map<string, string>();

        (usageData?.timelineEvents ?? []).forEach((event) => {
            const name = event.resourceName?.trim();

            if (name && !names.has(event.resourceId)) {
                names.set(event.resourceId, name);
            }
        });

        return names;
    }, [usageData?.timelineEvents]);
    const resourceActivityRows = useMemo(
        () =>
            (usageData?.resources ?? []).map((resource) => ({
                ...resource,
                resourceName: resource.resourceName?.trim() || resourceNameById.get(resource.resourceId),
            })),
        [resourceNameById, usageData?.resources],
    );

    const timeline = useMemo(() => usageData?.dailyUsage ?? [], [usageData?.dailyUsage]);
    const resourceIds = useMemo(
        () => Array.from(new Set(timeline.map((entry) => entry.resourceId).filter(Boolean))),
        [timeline],
    );
    const resourceColors = useMemo(
        () => [token.cyan4, token.blue4, token.green4, token.purple4, token.gold4],
        [token.blue4, token.cyan4, token.gold4, token.green4, token.purple4],
    );
    const resourceColorMap = useMemo(
        () =>
            resourceIds.reduce<Record<string, string>>((acc, id, index) => {
                acc[id] = resourceColors[index % resourceColors.length];

                return acc;
            }, {}),
        [resourceColors, resourceIds],
    );
    const resourceFillMap = useMemo(() => {
        const fillMap: Record<string, string> = {};

        Object.keys(resourceColorMap).forEach((resourceId) => {
            fillMap[resourceId] = `url(#gradient-${resourceId})`;
        });

        return fillMap;
    }, [resourceColorMap]);
    const resourceLabelMap = useMemo(
        () =>
            resourceIds.reduce<Record<string, string>>((acc, id) => {
                acc[id] = capitalCase(resourceIdToType[id] || id);

                return acc;
            }, {}),
        [resourceIdToType, resourceIds],
    );
    const transformedChartData = useMemo(
        () =>
            timeline.reduce<Record<string, string | number>[]>((acc, item) => {
                let day = acc.find((entry) => entry.date === item.date);

                if (!day) {
                    day = { date: item.date };
                    acc.push(day);
                }

                day[item.resourceId] = Number(day[item.resourceId] || 0) + (item.eventCount || 0);

                return acc;
            }, []),
        [timeline],
    );
    const activityTimelineEvents = useMemo<WorkspaceActivityTimelineEvent[]>(() => {
        if (usageData?.timelineEvents?.length) {
            return usageData.timelineEvents;
        }

        return timeline.map((entry, index) => ({
            action: entry.eventCount === 1 ? 'recorded an event on' : `recorded ${entry.eventCount} events on`,
            eventType: 'Activity',
            id: `usage-${entry.date}-${entry.resourceId}-${index}`,
            resourceId: entry.resourceId,
            resourceName: resourceLabelMap[entry.resourceId] ?? capitalCase(entry.resourceId),
            resourceType: resourceIdToType[entry.resourceId] ?? 'workspace resource',
            timestamp: new Date(`${entry.date}T12:00:00.000Z`).toISOString(),
        }));
    }, [resourceIdToType, resourceLabelMap, timeline, usageData]);
    const availableCadences = useMemo<ActivityTimelineCadenceId[]>(
        () =>
            planTier === 'enterprise'
                ? [
                      'day',
                      'multi-day',
                      'week',
                      'multi-week',
                      'month',
                      'quarter',
                      'multi-month',
                      'multi-quarter',
                      'year',
                      'multi-year',
                      'all-time',
                  ]
                : [
                      'day',
                      'multi-day',
                      'week',
                      'multi-week',
                      'month',
                      'quarter',
                      'multi-month',
                      'multi-quarter',
                      'year',
                  ],
        [planTier],
    );

    const handleTimelineCadenceChange = (cadence: ActivityTimelineCadenceId) => {
        setTimelineCadence(cadence);

        const timeframeByCadence: Record<ActivityTimelineCadenceId, UsageTimeFrame> = {
            day: UsageTimeFrame.WEEK,
            'multi-day': UsageTimeFrame.WEEK,
            week: UsageTimeFrame.WEEK,
            'multi-week': UsageTimeFrame.MONTH,
            month: UsageTimeFrame.MONTH,
            quarter: UsageTimeFrame.QUARTER,
            'multi-month': UsageTimeFrame.YEAR,
            'multi-quarter': UsageTimeFrame.YEAR,
            year: UsageTimeFrame.YEAR,
            'multi-year': UsageTimeFrame.ALL_TIME,
            'all-time': UsageTimeFrame.ALL_TIME,
        };

        setTimeFrame(timeframeByCadence[cadence]);
    };
    const handlePeriodChange = (nextTimeFrame: UsageTimeFrame) => {
        const cadenceByTimeframe: Record<UsageTimeFrame, ActivityTimelineCadenceId> = {
            [UsageTimeFrame.WEEK]: 'week',
            [UsageTimeFrame.MONTH]: 'month',
            [UsageTimeFrame.QUARTER]: 'quarter',
            [UsageTimeFrame.YEAR]: 'year',
            [UsageTimeFrame.ALL_TIME]: 'all-time',
        };

        setTimeFrame(nextTimeFrame);
        setTimelineCadence(cadenceByTimeframe[nextTimeFrame]);
    };

    return (
        <FullHeightFlexColumn className='usage-analytics-layout'>
            <div
                data-usage-analytics-surface='true'
                style={{
                    boxSizing: 'border-box',
                    flex: '1 1 0%',
                    minHeight: 0,
                    overflowY: 'auto',
                    padding: '0 0 0.75rem',
                }}
            >
                <AdvancedSettingsViewHeader
                    description='Review workspace participation, collaboration, and resource activity across the selected period.'
                    title='Usage Analytics'
                />
                {hasMinimumPlan('plus') ? (
                    <>
                        <PlatformAlert
                            styles={{ marginBottom: '0.75rem' }}
                            status='info'
                            title='About workspace events'
                            description={
                                <>
                                    An event represents any action taken on a workspace resource. Learn more about what
                                    events we capture in <OurDocsLink />
                                </>
                            }
                        />
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <AnalyticsSectionCard
                                ariaLabel='Workspace usage overview'
                                contentStyle={{ padding: '0.75rem' }}
                                description='Review workspace participation, collaboration, and resource activity for the selected period.'
                                extra={<UsagePeriodDropdown value={timeFrame} onChange={handlePeriodChange} />}
                                icon={<TbChartPie />}
                                title='Overview'
                            >
                                {loading && !usageData ? (
                                    <UsageOverviewSkeleton />
                                ) : (
                                    <UsageOverviewStats usageData={usageData} />
                                )}
                            </AnalyticsSectionCard>
                            <UsageTimelineChart
                                loading={loading && !usageData}
                                resourceColorMap={resourceColorMap}
                                resourceFillMap={resourceFillMap}
                                resourceIds={resourceIds}
                                resourceLabelMap={resourceLabelMap}
                                transformedChartData={transformedChartData}
                            />
                            <UsageResourcesTable loading={loading && !usageData} resources={resourceActivityRows} />
                            <WorkspaceActivityTimeline
                                availableCadences={availableCadences}
                                cadence={timelineCadence}
                                error={usageError && !usageData}
                                events={activityTimelineEvents}
                                loading={loading && !usageData}
                                onCadenceChange={handleTimelineCadenceChange}
                                onRetry={() => setRetryCount((current) => current + 1)}
                                startDate={workspace?.createdAt}
                            />
                            <UsageLocationsMap
                                geoLocations={usageData?.geoLocations ?? []}
                                loading={loading && !usageData}
                                mapStyle={mapTileType}
                                popupInfo={popupInfo}
                                setPopupInfo={setPopupInfo}
                            />
                        </div>
                    </>
                ) : (
                    <NotAvailable planRequired='plus' height='25rem' />
                )}
            </div>
            <div
                data-usage-analytics-footer='true'
                style={{ flex: 'none', paddingBottom: '0.5rem', textAlign: 'center', width: '100%' }}
            >
                <Divider style={{ margin: '0 0 0.5rem' }} />
                <LearnMoreFooter align='center' topic='workspace usage analytics and event reporting' />
            </div>
        </FullHeightFlexColumn>
    );
};

export default Usage;
